import styled from '@emotion/styled';

import { focusRing, forMobile, hoverable, scrollbarStyles, theme } from '@/design-system';
import { typographyVariantStyles } from '@/design-system/typography-variants';

const STAT_CARDS_PER_ROW = 4;
const STAT_CARD_GAP = theme.spacing[8];
const STAT_CARD_MIN_WIDTH = 148;

export const TabRoot = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
  paddingTop: theme.spacing[16],
  minWidth: 0,
});

export const FilterBar = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing[8],

  [forMobile]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
});

export const FilterSlot = styled.div({
  width: 200,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const ManagerSlot = styled.div({
  width: 260,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const RangeSlot = styled.div({
  width: 280,
  flexShrink: 0,

  [forMobile]: {
    width: '100%',
  },
});

export const Section = styled.section({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  minWidth: 0,
});

export const SectionHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minHeight: 32,
});

export const NameStack = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
});

export const SectionTitle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  minWidth: 0,
});

export const SectionActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  flexShrink: 0,
});

export const StatCardRow = styled.div({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(max(${STAT_CARD_MIN_WIDTH}px, calc((100% - ${(STAT_CARDS_PER_ROW - 1) * STAT_CARD_GAP}px) / ${STAT_CARDS_PER_ROW})), 1fr))`,
  gap: STAT_CARD_GAP,
});

const statCardBase = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.spacing[4],
  minHeight: 92,
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  border: '1px solid transparent',
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
  textAlign: 'left',
} as const;

export const StatCard = styled.div(statCardBase);

export const StatCardButton = styled.button({
  ...statCardBase,
  margin: 0,
  fontFamily: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'border'),
  ...focusRing(theme),

  '&[aria-pressed="true"]': {
    borderColor: theme.colors.border.brand,
    backgroundColor: theme.colors.brandAlpha[8],
  },
});

export const StatCardLabel = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[6],
  color: theme.colors.text.secondary,
  minWidth: 0,
});

export const ContactList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
});

export const ContactCard = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[12],
  padding: `${theme.spacing[12]}px ${theme.spacing[16]}px`,
  borderRadius: theme.radius[12],
  border: `1px solid ${theme.colors.border.subtle}`,
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
});

export const ContactHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[12],
  minWidth: 0,

  [forMobile]: {
    alignItems: 'flex-start',
  },
});

export const ContactTitle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minWidth: 0,

  '& > a': {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
});

export const ContactMeta = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const ContactEntries = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  paddingLeft: theme.spacing[32],

  [forMobile]: {
    paddingLeft: 0,
  },
});

export const EntryMetaRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const EntryText = styled.div({
  ...typographyVariantStyles.body3,
  color: theme.colors.text.primary,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

export const QuietLine = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[8],
  paddingTop: theme.spacing[16],
  paddingBottom: theme.spacing[16],
  textAlign: 'center',
});

export const SkeletonBar = styled.div<{ w?: string; h?: number }>(({ w, h }) => ({
  height: h ?? theme.spacing[12],
  width: w ?? '100%',
  borderRadius: theme.radius[4],
  background: theme.colors.background.overlay,
  animation: 'gpTeamActivityPulse 1.4s ease-in-out infinite',
  '@keyframes gpTeamActivityPulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 1 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const MemberPickerPositioner = styled.div({
  zIndex: 1400,
});

export const MemberPickerPopup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  width: 'max(var(--anchor-width), 280px)',
  maxWidth: 'var(--available-width)',
  maxHeight: 'min(420px, var(--available-height))',
  borderRadius: theme.radius[8],
  border: `1px solid ${theme.colors.border.default}`,
  backgroundColor: theme.colors.background.raised,
  boxShadow: theme.shadows.md,
  outline: 'none',
  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.97) translateY(-4px)',
  },
});

export const MemberPickerSearch = styled.div({
  flexShrink: 0,
  padding: theme.spacing[8],
  borderBottom: `1px solid ${theme.colors.border.subtle}`,
});

export const MemberPickerList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing[8],
  ...scrollbarStyles,
});

export const MemberPickerRow = styled.button({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  minHeight: 36,
  margin: 0,
  padding: `${theme.spacing[6]}px ${theme.spacing[8]}px`,
  border: 'none',
  borderRadius: theme.radius[8],
  backgroundColor: 'transparent',
  color: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'surface'),
  ...focusRing(theme),

  '&[aria-checked="true"]': {
    backgroundColor: theme.colors.brandAlpha[8],
  },
});

export const MemberPickerRowText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
});

export const ChartCard = styled.section({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
});

export const ChartHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing[12],
  minWidth: 0,
});

export const LegendList = styled.ul({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing[16],
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

export const LegendItem = styled.li({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing[6],
});

export const Swatch = styled.span<{ color: string }>(({ color }) => ({
  display: 'inline-block',
  width: theme.spacing[8],
  height: theme.spacing[8],
  borderRadius: theme.radius[2],
  backgroundColor: color,
  flexShrink: 0,
}));

export const ChartArea = styled.div<{ h: number; clickable?: boolean }>(({ h, clickable }) => ({
  height: h,
  minWidth: 0,
  '& .recharts-bar-rectangle': { cursor: clickable ? 'pointer' : 'default' },
  '& .recharts-surface:focus, & .recharts-wrapper:focus': { outline: 'none' },
}));

export const ChartTooltipBox = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  minWidth: 180,
  padding: `${theme.spacing[8]}px ${theme.spacing[12]}px`,
  borderRadius: theme.radius[8],
  border: `1px solid ${theme.colors.border.default}`,
  backgroundColor: theme.colors.background.canvas,
  boxShadow: theme.shadows.md,
});

export const TooltipRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[12],
});

export const KpiGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: theme.spacing[8],
});

export const KpiTile = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  padding: theme.spacing[16],
  borderRadius: theme.radius[16],
  backgroundColor: theme.colors.background.raised,
  minWidth: 0,
});

export const KpiValueRow = styled.div({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: theme.spacing[8],
});

export const ChangeText = styled.span<{ tone: 'up' | 'down' | 'flat' }>(({ tone }) => ({
  ...typographyVariantStyles.body4,
  fontWeight: 500,
  color: {
    up: theme.colors.success.text,
    down: theme.colors.error.text,
    flat: theme.colors.text.secondary,
  }[tone],
  whiteSpace: 'nowrap',
}));

export const Sparkline = styled.div({
  height: theme.spacing[32],
  marginTop: theme.spacing[4],
});

export const TwoColumns = styled.div({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
  alignItems: 'start',
  gap: theme.spacing[16],

  [forMobile]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
});

export const BarList = styled.ol({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

export const BarRowButton = styled.button({
  display: 'grid',
  gridTemplateColumns: 'minmax(96px, 140px) minmax(0, 1fr)',
  alignItems: 'center',
  gap: theme.spacing[12],
  width: '100%',
  minHeight: theme.spacing[24],
  margin: 0,
  padding: `${theme.spacing[2]}px ${theme.spacing[8]}px`,
  border: 'none',
  borderRadius: theme.radius[8],
  backgroundColor: 'transparent',
  color: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'surface'),
  ...focusRing(theme),
});

export const BarTrack = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const BarFill = styled.span<{ share: number; color: string }>(({ share, color }) => ({
  display: 'block',
  height: theme.spacing[12],
  width: `${Math.max(share * 100, share > 0 ? 1 : 0)}%`,
  borderRadius: `0 ${theme.radius[4]}px ${theme.radius[4]}px 0`,
  backgroundColor: color,
  flexShrink: 0,
}));

export const HeatmapScroller = styled.div({
  overflowX: 'auto',
  minWidth: 0,
  ...scrollbarStyles,
});

export const HeatmapGrid = styled.div<{ columns: number }>(({ columns }) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(96px, 140px) repeat(${columns}, minmax(10px, 1fr))`,
  alignItems: 'center',
  columnGap: theme.spacing[2],
  rowGap: theme.spacing[2],
  minWidth: 140 + columns * 12,
}));

export const HeatCell = styled.span<{ color: string }>(({ color }) => ({
  display: 'block',
  aspectRatio: '1 / 1',
  minHeight: theme.spacing[12],
  maxHeight: theme.spacing[24],
  borderRadius: theme.radius[2],
  backgroundColor: color,
  transition: 'opacity 120ms ease',
  ...focusRing(theme, { inset: false }),

  '&[data-clickable]': {
    cursor: 'pointer',
  },

  '&[data-dimmed]': {
    opacity: 0.35,
  },

  '&[data-active]': {
    position: 'relative',
    zIndex: 1,
    boxShadow: `0 0 0 2px ${theme.colors.background.raised}, 0 0 0 3px ${theme.colors.text.primary}`,
  },
}));

export const HeatmapFrame = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  minWidth: 0,
});

export const HeatmapTooltip = styled.div({
  position: 'absolute',
  zIndex: 2,
  pointerEvents: 'none',
});

export const HeatNameButton = styled.button({
  ...typographyVariantStyles.body4,
  margin: 0,
  padding: `${theme.spacing[2]}px ${theme.spacing[4]}px`,
  border: 'none',
  borderRadius: theme.radius[4],
  backgroundColor: 'transparent',
  color: theme.colors.text.primary,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  appearance: 'none',
  ...hoverable(theme, 'surface'),
  ...focusRing(theme),

  '&[data-active]': {
    fontWeight: 600,
    color: theme.colors.brand.text,
  },
});

export const DrawerBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
  minWidth: 0,
});

export const GoalRow = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[6],
});

export const GoalEditorRow = styled.div({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 1fr) auto',
  alignItems: 'end',
  gap: theme.spacing[8],
});
